// Text geometry for CoLOS Text/Date fields — pure module (no window/document).
//
// Evidence-based model (verified against the 21 real labels in tests/fixtures/corpus: with this
// model no Arial field crosses the label edge, while the legacy "chars × CharWidth" model put
// 20 fields up to 77 mm past it):
//
// * CharHeight is the font CELL height (winAscent + winDescent), not the em size:
//     em = CharHeight / ((winAscent + winDescent) / unitsPerEm)      (Arial: / 1.1171875)
// * CharWidth is a horizontal scale control. CoLOS's default CharWidth for Arial is ≈ 0.8692 × CharHeight
//   (corpus: 1269/1460, 423/487, 847/974); for the printer's 'Arabic' face ≈ 0.8225 (635/772).
//     xScale = CharWidth / (DEFAULT_WIDTH_RATIO[typeface] × CharHeight)
// * Advance of a string = Σ glyphAdvance / unitsPerEm × em × xScale (real Arial / Arial Bold tables).
//
// All lengths are file units (HIMETRIC, 1/100 mm).

import * as ARIAL from './arial-metrics.js';

/**
 * Anchor of (Geometry.X, Geometry.Y) relative to the text box.
 * PENDING CONFIRMATION against CoLOS Designer rendering: we currently treat the field origin as the
 * TOP-LEFT corner of the text cell (cell height = CharHeight). The renderer should place the SVG
 * text baseline at y + baselineOffset(font). If golden files show a different anchor (e.g. baseline
 * or bottom-left), change it here and in textBox() only.
 */
export const ANCHOR = 'top-left';

export const DEFAULT_WIDTH_RATIO = Object.freeze({ Arial: 0.8692, Arabic: 0.8225 });

export const FONT_METRICS = Object.freeze({
  unitsPerEm: ARIAL.unitsPerEm,
  winAscent: ARIAL.winAscent,
  winDescent: ARIAL.winDescent,
});

/** (winAscent + winDescent) / unitsPerEm — 1.1171875 for Arial. */
export const CELL_TO_EM = (ARIAL.winAscent + ARIAL.winDescent) / ARIAL.unitsPerEm;

const FAMILIES = {
  Arial: 'Arial, "Liberation Sans", Helvetica, sans-serif',
  Arabic: '"Segoe UI", Tahoma, "Arabic Typesetting", sans-serif',
};

function ratioFor(typeface) {
  return DEFAULT_WIDTH_RATIO[typeface] ?? DEFAULT_WIDTH_RATIO.Arial;
}

/** Default CoLOS CharWidth for a typeface at a CharHeight (integer units). */
export function defaultCharWidth(typeface, height) {
  return Math.round(ratioFor(typeface) * height);
}

/** Em size in units for a font whose CharHeight is the cell height. */
export function emSize(font) {
  return (Number(font?.height) || 0) / CELL_TO_EM;
}

/** Horizontal scale implied by CharWidth (1 when CharWidth is the CoLOS default or missing). */
export function xScale(font) {
  const h = Number(font?.height) || 0;
  const w = Number(font?.width);
  if (!h || !w) return 1;
  return w / (ratioFor(font.typeface) * h);
}

/** Distance from the top of the cell to the baseline, in units. */
export function baselineOffset(font) {
  return emSize(font) * ARIAL.winAscent / ARIAL.unitsPerEm;
}

/** CSS font-family stack for a CoLOS typeface. */
export function fontFamily(typeface) {
  return FAMILIES[typeface] ?? `"${String(typeface || 'Arial').replace(/"/g, '')}", ${FAMILIES.Arial}`;
}

/** CSS font shorthand, e.g. `bold 100px Arial, "Liberation Sans", Helvetica, sans-serif`. */
export function cssFont(font, sizePx = 100) {
  const parts = [];
  if (font?.italic) parts.push('italic');
  if (font?.bold) parts.push('bold');
  parts.push(`${sizePx}px`, fontFamily(font?.typeface));
  return parts.join(' ');
}

// ---- measurer for glyphs not in the tables -------------------------------------------------

/** Node fallback: 0.5 em per character (returns px at a 100px font). */
const fallbackMeasurer = (text) => 50 * Array.from(text).length;
let measurer = fallbackMeasurer;

/**
 * Inject a measurer for non-table fonts/glyphs: fn(text, cssFont) -> width in px at a 100px font.
 * The browser passes canvas measureText. Call with no argument to restore the fallback.
 */
export function setMeasurer(fn) {
  measurer = typeof fn === 'function' ? fn : fallbackMeasurer;
}

function tableAdvance(cp, bold) {
  if (cp >= ARIAL.first && cp <= 255) {
    const v = (bold ? ARIAL.bold : ARIAL.regular)[cp - ARIAL.first];
    return v || 0;
  }
  return (bold ? ARIAL.extraBold : ARIAL.extraRegular)[cp] || 0;
}

/**
 * True when textAdvance() for this font is authoritative: only the embedded Arial / Arial Bold tables are.
 * Every other typeface goes through the injected measurer (a browser font standing in for a printer font —
 * the printer's 'Arabic' face is a CoLOS bitmap font, arabic6.fnt/arabic9.fnt). With `text`, also requires
 * every glyph to be in the table (Arial text with e.g. Arabic glyphs is measured, so estimated).
 */
export function isWidthExact(font, text) {
  if (!font || font.typeface !== 'Arial') return false;
  if (text == null) return true;
  for (const ch of String(text)) if (!tableAdvance(ch.codePointAt(0), !!font.bold)) return false;
  return true;
}

/** Width of `text` in units for `font` ({typeface, height, width, bold, italic}). */
export function textAdvance(text, font) {
  const str = String(text ?? '');
  if (!str || !font) return 0;
  const em = emSize(font);
  const scale = xScale(font);
  const css = cssFont(font, 100);
  const measure = (s) => ((Number(measurer(s, css)) || 0) / 100) * em; // px@100px → em fraction × em

  if (font.typeface !== 'Arial') return measure(str) * scale;

  // Arial: table advances; runs of glyphs missing from the table go through the measurer together
  // (so shaping scripts such as Arabic are measured as a run).
  let designUnits = 0;
  let unknownRun = '';
  let other = 0;
  for (const ch of str) {
    const adv = tableAdvance(ch.codePointAt(0), !!font.bold);
    if (adv) {
      if (unknownRun) { other += measure(unknownRun); unknownRun = ''; }
      designUnits += adv;
    } else {
      unknownRun += ch;
    }
  }
  if (unknownRun) other += measure(unknownRun);
  return ((designUnits / ARIAL.unitsPerEm) * em + other) * scale;
}

/**
 * Approximate printed text for a date field when no preview is supplied: tokens replaced by
 * representative widest-ish values (%D→28, %M→12, %Y→2026, %y→26, %H/%h→23, %m/%N→59, other %x→00),
 * bracket parameters like [0,2,2,0] removed. Callers with dates.formatDate should pass real text.
 */
export function sampleDateText(format) {
  const map = { D: '28', M: '12', Y: '2026', y: '26', H: '23', h: '23', m: '59', N: '59', S: '59', s: '59' };
  return String(format ?? '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/%(.)/g, (_, t) => (t === '%' ? '%' : map[t] ?? '00'));
}

/**
 * Text box relative to the field origin (see ANCHOR), unrotated: { x:0, y:0, w, h }.
 * `text` overrides the displayed string (e.g. a date preview); defaults to field.text for text
 * fields and sampleDateText(field.format) for date fields.
 */
export function textBox(field, text) {
  const font = field?.font || {};
  let s = text;
  if (s == null) s = field?.kind === 'date' ? sampleDateText(field.format) : field?.text ?? '';
  return { x: 0, y: 0, w: textAdvance(s, font), h: Number(font.height) || 0 };
}
